import React from 'react';
import { motion } from 'framer-motion';
import { AdvisorAvatar } from './AdvisorAvatar';

const scenarios = [
  {
    title: "Career Transition",
    description: "Navigating a major career change",
    example: "Should I leave my corporate job to start my own business?",
    advisors: ["Beatrice", "Samuel"],
    outcome: "Get strategic analysis from Beatrice on market viability while Samuel helps you understand your personal readiness and growth potential."
  },
  {
    title: "Leadership Challenge",
    description: "Building and managing teams",
    example: "How do I handle a difficult team member who's affecting morale?",
    advisors: ["Samuel", "Marcus"],
    outcome: "Samuel provides empathetic leadership strategies while Marcus offers stoic wisdom on maintaining composure and making tough decisions."
  },
  {
    title: "Innovation Project",
    description: "Launching something new",
    example: "I have an idea for a product but don't know where to start",
    advisors: ["Leo", "Beatrice"],
    outcome: "Leo helps you think creatively and challenge assumptions while Beatrice provides a systematic framework for execution."
  }
];

export const ScenariosSection: React.FC = () => {
  return (
    <div id="scenarios" className="px-4 py-12 sm:py-16 md:py-24">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-orange-400 to-purple-400 bg-clip-text text-transparent">
            Real Scenarios. Real Results.
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto px-2">
            See how different combinations of advisors tackle complex decisions across various life situations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {scenarios.map((scenario, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm rounded-3xl p-4 sm:p-6 md:p-8 border border-white/10 hover:bg-white/10 transition-all duration-300"
            >
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3 sm:mb-4">{scenario.title}</h3>
              <p className="text-white/70 mb-3 sm:mb-4 text-sm sm:text-base">{scenario.description}</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6">
                <p className="text-orange-200 italic text-sm sm:text-base">"{scenario.example}"</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                {scenario.advisors.map((advisorName) => (
                  <AdvisorAvatar key={advisorName} name={advisorName} size="small" />
                ))}
                <span className="text-white/60 text-xs sm:text-sm">
                  {scenario.advisors.join(' + ')}
                </span>
              </div>
              <p className="text-white/80 text-xs sm:text-sm leading-relaxed">{scenario.outcome}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};